import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Play, TestTube, Edit, Trash2, Power, PowerOff,
  Sparkles, CheckCircle, AlertCircle, Clock, TrendingUp, Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONDITION_TYPES = [
  { value: "sender", label: "Specific Sender", description: "Email from exact address" },
  { value: "sender_domain", label: "Sender Domain", description: "Email from domain (e.g., @marketing.com)" },
  { value: "sender_contains", label: "Sender Contains", description: "Sender contains text" },
  { value: "subject_contains", label: "Subject Contains", description: "Subject contains keyword" },
  { value: "body_contains", label: "Body Contains", description: "Email body contains keyword" },
  { value: "subject_or_body_contains", label: "Subject OR Body Contains", description: "Either contains keyword" },
  { value: "has_attachment", label: "Has Attachment", description: "Email has attachments" },
  { value: "older_than_days", label: "Older Than Days", description: "Email older than X days" },
  { value: "date_range", label: "Date Range", description: "Between two dates" },
  { value: "is_unread", label: "Is Unread", description: "Unread emails only" },
  { value: "is_read", label: "Is Read", description: "Read emails only" },
  { value: "has_tag", label: "Has Tag", description: "Email has specific tag" },
  { value: "no_tags", label: "No Tags", description: "Email has no tags" },
];

const ACTION_TYPES = [
  { value: "apply_tag", label: "Apply Tag", requiresValue: true },
  { value: "remove_tag", label: "Remove Tag", requiresValue: true },
  { value: "delete", label: "Delete Email", requiresValue: false },
  { value: "archive", label: "Archive Email", requiresValue: false },
  { value: "mark_read", label: "Mark as Read", requiresValue: false },
  { value: "mark_unread", label: "Mark as Unread", requiresValue: false },
  { value: "mark_important", label: "Mark as Important", requiresValue: false },
  { value: "create_ai_task", label: "Create AI Task", requiresValue: true },
  { value: "auto_draft_reply", label: "🤖 Auto-Draft Reply", requiresValue: true, isDraft: true },
];

const TAG_COLORS = ["red", "orange", "yellow", "green", "blue", "purple", "pink", "gray"];

export default function AutomationRulesV2() {
  const { actorId } = useParams();
  const navigate = useNavigate();

  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [selectedRules, setSelectedRules] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    conditions: [{ type: "sender", value: "", operator: "AND" }],
    actions: [{ type: "apply_tag", value: "", metadata: {} }],
    logical_operator: "AND",
    active: true,
    priority: "medium",
    stop_processing: false,
    apply_to_future: true,
    apply_to_existing: false,
    apply_to_server: true,
  });

  useEffect(() => {
    fetchRules();
    fetchTemplates();
    fetchSuggestions();
  }, [actorId]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/ai-email/rules-v2/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRules(response.data.rules || []);
    } catch (error) {
      console.error("Failed to fetch rules", error);
      toast.error("Failed to load automation rules");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/ai-email/rule-templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch templates", error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/ai-email/rule-suggestions/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Failed to fetch suggestions", error);
    }
  };

  const createRule = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-email/rules-v2/${actorId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rule created successfully");
      setShowCreateForm(false);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error("Failed to create rule", error);
      toast.error("Failed to create rule");
    }
  };

  const updateRule = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/ai-email/rules-v2/${editingRule.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rule updated successfully");
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error("Failed to update rule", error);
      toast.error("Failed to update rule");
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/ai-email/rules-v2/${ruleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rule deleted");
      fetchRules();
    } catch (error) {
      console.error("Failed to delete rule", error);
      toast.error("Failed to delete rule");
    }
  };

  const toggleRule = async (ruleId, active) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API}/admin/ai-email/rules-v2/${ruleId}/toggle?active=${active}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rule ${active ? "activated" : "deactivated"}`);
      fetchRules();
    } catch (error) {
      console.error("Failed to toggle rule", error);
      toast.error("Failed to toggle rule");
    }
  };

  const runRule = async (ruleId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-email/rules-v2/${ruleId}/run?actor_id=${actorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rule execution started - this may take a few moments");
      setTimeout(fetchRules, 3000);
    } catch (error) {
      console.error("Failed to run rule", error);
      toast.error("Failed to run rule");
    }
  };

  const testRule = async (ruleId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/ai-email/rules-v2/${ruleId}/test?actor_id=${actorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResults(response.data);
      toast.success(`Found ${response.data.matched_count} matching emails`);
    } catch (error) {
      console.error("Failed to test rule", error);
      toast.error("Failed to test rule");
    }
  };

  const createFromTemplate = async (template) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-email/rules-v2/from-template/${actorId}?template_id=${template.id}`,
        {
          name: template.name,
          description: template.description,
          active: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rule created from template: ${template.name}`);
      setShowTemplates(false);
      fetchRules();
    } catch (error) {
      console.error("Failed to create from template", error);
      toast.error("Failed to create rule from template");
    }
  };

  const bulkToggle = async (active) => {
    if (selectedRules.length === 0) {
      toast.error("No rules selected");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-email/rules-v2/bulk-toggle?active=${active}`,
        selectedRules,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedRules.length} rules ${active ? "activated" : "deactivated"}`);
      setSelectedRules([]);
      fetchRules();
    } catch (error) {
      console.error("Failed to bulk toggle", error);
      toast.error("Failed to bulk toggle rules");
    }
  };

  const bulkDelete = async () => {
    if (selectedRules.length === 0) {
      toast.error("No rules selected");
      return;
    }

    if (!window.confirm(`Delete ${selectedRules.length} rules?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/ai-email/rules-v2/bulk-delete`,
        {
          data: selectedRules,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`${selectedRules.length} rules deleted`);
      setSelectedRules([]);
      fetchRules();
    } catch (error) {
      console.error("Failed to bulk delete", error);
      toast.error("Failed to bulk delete rules");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      conditions: [{ type: "sender", value: "", operator: "AND" }],
      actions: [{ type: "apply_tag", value: "", metadata: {} }],
      logical_operator: "AND",
      active: true,
      priority: "medium",
      stop_processing: false,
      apply_to_future: true,
      apply_to_existing: false,
      apply_to_server: true,
    });
    setShowCreateForm(false);
    setEditingRule(null);
  };

  const addCondition = () => {
    setFormData({
      ...formData,
      conditions: [...formData.conditions, { type: "sender", value: "", operator: "AND" }]
    });
  };

  const removeCondition = (index) => {
    setFormData({
      ...formData,
      conditions: formData.conditions.filter((_, i) => i !== index)
    });
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...formData.conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setFormData({ ...formData, conditions: newConditions });
  };

  const addAction = () => {
    setFormData({
      ...formData,
      actions: [...formData.actions, { type: "apply_tag", value: "", metadata: {} }]
    });
  };

  const removeAction = (index) => {
    setFormData({
      ...formData,
      actions: formData.actions.filter((_, i) => i !== index)
    });
  };

  const updateAction = (index, field, value) => {
    const newActions = [...formData.actions];
    newActions[index] = { ...newActions[index], [field]: value };
    setFormData({ ...formData, actions: newActions });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/ai/actors/${actorId}/emails`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Emails
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Zap className="w-6 h-6" />
              Automation Rules
            </h1>
            <p className="text-sm text-muted-foreground">
              Automate email organization with powerful rules
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowTemplates(!showTemplates)}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Templates
          </Button>
          <Button onClick={() => setShowCreateForm(!showCreateForm)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Rules</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{rules.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Rules</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {rules.filter(r => r.active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Processed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {rules.reduce((sum, r) => sum + (r.statistics?.total_processed || 0), 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Suggestions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {suggestions.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Suggested Rules
            </CardTitle>
            <CardDescription>
              Based on your email patterns, we recommend these automation rules
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {suggestions.slice(0, 3).map((suggestion, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border">
                  <div className="flex-1">
                    <p className="font-medium">{suggestion.rule_template.name}</p>
                    <p className="text-sm text-muted-foreground">{suggestion.rule_template.reasoning}</p>
                    <Badge variant="secondary" className="mt-1">
                      {Math.round(suggestion.confidence * 100)}% confidence
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => createFromTemplate({
                      id: `suggestion_${idx}`,
                      ...suggestion.rule_template
                    })}
                  >
                    Create Rule
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Rule Templates</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowTemplates(false)}>
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 border rounded hover:bg-gray-50 cursor-pointer"
                  onClick={() => createFromTemplate(template)}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{template.icon}</span>
                    <div className="flex-1">
                      <h3 className="font-medium">{template.name}</h3>
                      <p className="text-sm text-muted-foreground">{template.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="secondary">{template.category}</Badge>
                        {template.popular && <Badge variant="default">Popular</Badge>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Form */}
      {(showCreateForm || editingRule) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{editingRule ? "Edit Rule" : "Create New Rule"}</CardTitle>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Rule Name*</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Archive Marketing Emails"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows={2}
                />
              </div>
            </div>

            {/* Conditions */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Conditions</label>
                <Select
                  value={formData.logical_operator}
                  onValueChange={(value) => setFormData({ ...formData, logical_operator: value })}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">Match ALL</SelectItem>
                    <SelectItem value="OR">Match ANY</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.conditions.map((condition, index) => (
                <div key={index} className="flex gap-2 items-start p-3 border rounded">
                  <div className="flex-1 space-y-2">
                    <Select
                      value={condition.type}
                      onValueChange={(value) => updateCondition(index, "type", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {!["is_unread", "is_read", "no_tags"].includes(condition.type) && (
                      <Input
                        value={condition.value || ""}
                        onChange={(e) => updateCondition(index, "value", e.target.value)}
                        placeholder={
                          condition.type === "sender" ? "email@example.com" :
                          condition.type === "sender_domain" ? "marketing.com" :
                          condition.type === "older_than_days" ? "30" :
                          "Enter value"
                        }
                      />
                    )}
                  </div>
                  {formData.conditions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCondition(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={addCondition}>
                <Plus className="w-4 h-4 mr-2" />
                Add Condition
              </Button>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <label className="block text-sm font-medium">Actions</label>

              {formData.actions.map((action, index) => (
                <div key={index} className="flex gap-2 items-start p-3 border rounded">
                  <div className="flex-1 space-y-2">
                    <Select
                      value={action.type}
                      onValueChange={(value) => updateAction(index, "type", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACTION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {ACTION_TYPES.find(t => t.value === action.type)?.requiresValue && (
                      <div className="space-y-2">
                        {action.type === "auto_draft_reply" ? (
                          <>
                            <label className="text-sm font-medium text-gray-700">
                              📝 Draft Instructions (for AI)
                            </label>
                            <Textarea
                              value={action.draft_instructions || action.value || ""}
                              onChange={(e) => {
                                updateAction(index, "draft_instructions", e.target.value);
                                updateAction(index, "value", e.target.value);
                              }}
                              placeholder="E.g., Draft a friendly response offering my availability. Always include: I'm available Mon-Fri 9AM-5PM EST. Ask them to suggest 2-3 time slots."
                              rows={4}
                              className="w-full"
                            />
                            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                              <Checkbox
                                checked={action.auto_send || false}
                                onCheckedChange={(checked) => updateAction(index, "auto_send", checked)}
                              />
                              <div>
                                <label className="text-sm font-medium text-amber-900">
                                  Auto-send without review
                                </label>
                                <p className="text-xs text-amber-700">
                                  ⚠️ Use with caution! Draft will be sent immediately without your approval.
                                </p>
                              </div>
                            </div>
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                              <p className="text-xs text-blue-800">
                                💡 <strong>Tip:</strong> Your personal drafting conditions will automatically be applied to this auto-draft.
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <Input
                              value={action.value || ""}
                              onChange={(e) => updateAction(index, "value", e.target.value)}
                              placeholder={
                                action.type === "apply_tag" ? "Tag name" :
                                action.type === "forward" ? "email@example.com" :
                                "Enter value"
                              }
                            />
                            {action.type === "apply_tag" && (
                              <div className="flex gap-2">
                                <label className="text-sm">Color:</label>
                                {TAG_COLORS.map((color) => (
                                  <button
                                    key={color}
                                    className={`w-6 h-6 rounded-full border-2 ${
                                      action.metadata?.color === color ? "border-black" : "border-gray-300"
                                    }`}
                                    style={{ backgroundColor: color }}
                                    onClick={() => updateAction(index, "metadata", { color })}
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {formData.actions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAction(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={addAction}>
                <Plus className="w-4 h-4 mr-2" />
                Add Action
              </Button>
            </div>

            {/* Settings */}
            <div className="space-y-3 pt-4 border-t">
              <label className="block text-sm font-medium">Settings</label>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.apply_to_future}
                    onCheckedChange={(checked) => setFormData({ ...formData, apply_to_future: checked })}
                  />
                  <label className="text-sm">Apply to future emails</label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.apply_to_existing}
                    onCheckedChange={(checked) => setFormData({ ...formData, apply_to_existing: checked })}
                  />
                  <label className="text-sm">Apply to existing emails now</label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.apply_to_server}
                    onCheckedChange={(checked) => setFormData({ ...formData, apply_to_server: checked })}
                  />
                  <label className="text-sm">Sync changes with email server</label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.stop_processing}
                    onCheckedChange={(checked) => setFormData({ ...formData, stop_processing: checked })}
                  />
                  <label className="text-sm">Stop processing other rules if this matches</label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Priority</label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button onClick={editingRule ? updateRule : createRule}>
                {editingRule ? "Update Rule" : "Create Rule"}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Operations */}
      {selectedRules.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {selectedRules.length} rule(s) selected
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => bulkToggle(true)}>
                  <Power className="w-4 h-4 mr-1" />
                  Enable
                </Button>
                <Button size="sm" variant="outline" onClick={() => bulkToggle(false)}>
                  <PowerOff className="w-4 h-4 mr-1" />
                  Disable
                </Button>
                <Button size="sm" variant="destructive" onClick={bulkDelete}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedRules([])}>
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rules List */}
      <div className="space-y-3">
        {rules.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Zap className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium mb-2">No automation rules yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first rule to automate email organization
              </p>
              <Button onClick={() => setShowCreateForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Rule
              </Button>
            </CardContent>
          </Card>
        ) : (
          rules.map((rule) => (
            <Card key={rule.id} className={!rule.active ? "opacity-60" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Checkbox
                      checked={selectedRules.includes(rule.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRules([...selectedRules, rule.id]);
                        } else {
                          setSelectedRules(selectedRules.filter(id => id !== rule.id));
                        }
                      }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-base">{rule.name}</CardTitle>
                        {rule.active ? (
                          <Badge variant="default" className="bg-green-500">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                        <Badge variant="outline">{rule.priority}</Badge>
                        {rule.template_id && <Badge variant="secondary">From Template</Badge>}
                      </div>
                      {rule.description && (
                        <p className="text-sm text-muted-foreground">{rule.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleRule(rule.id, !rule.active)}
                      title={rule.active ? "Deactivate" : "Activate"}
                    >
                      {rule.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => testRule(rule.id)}
                      title="Test rule"
                    >
                      <TestTube className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => runRule(rule.id)}
                      title="Run now"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingRule(rule);
                        setFormData(rule);
                      }}
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteRule(rule.id)}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Conditions */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      WHEN ({rule.logical_operator})
                    </p>
                    <div className="space-y-1">
                      {rule.conditions?.map((cond, idx) => (
                        <div key={idx} className="text-sm flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {CONDITION_TYPES.find(t => t.value === cond.type)?.label || cond.type}
                          </Badge>
                          {cond.value && <span className="text-muted-foreground">: {cond.value}</span>}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">THEN</p>
                    <div className="space-y-1">
                      {rule.actions?.map((action, idx) => (
                        <div key={idx} className="text-sm flex items-center gap-2">
                          <Badge variant="default" className="text-xs">
                            {ACTION_TYPES.find(t => t.value === action.type)?.label || action.type}
                          </Badge>
                          {action.value && (
                            <span className="text-muted-foreground">: {action.value}</span>
                          )}
                          {action.metadata?.color && (
                            <span
                              className="w-3 h-3 rounded-full inline-block"
                              style={{ backgroundColor: action.metadata.color }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                {rule.statistics && (
                  <div className="mt-4 pt-4 border-t flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {rule.statistics.total_matched || 0} matched
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {rule.statistics.total_processed || 0} processed
                    </div>
                    {rule.statistics.last_run && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last run: {new Date(rule.statistics.last_run).toLocaleDateString()}
                      </div>
                    )}
                    {rule.statistics.error_count > 0 && (
                      <div className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="w-3 h-3" />
                        {rule.statistics.error_count} errors
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Test Results Modal */}
      {testResults && (
        <Card className="border-blue-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Test Results</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setTestResults(null)}>
                Close
              </Button>
            </div>
            <CardDescription>
              Found {testResults.matched_count} matching emails out of {testResults.total_scanned} scanned
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {testResults.matched_emails?.map((email, idx) => (
                <div key={idx} className="p-3 border rounded hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{email.subject}</p>
                      <p className="text-xs text-muted-foreground">{email.from_addr}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {!email.is_read && <Badge variant="secondary">Unread</Badge>}
                      {email.tags?.map(tag => (
                        <Badge key={tag} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
